import { LegalLayout, LegalSection } from './LegalLayout';

export default function Terms() {
  return (
    <LegalLayout
      title="Términos y Condiciones de Servicio"
      metaDescription="Términos y Condiciones de FinScope: condiciones de uso de la plataforma SaaS de gestión y análisis financiero empresarial."
      lastUpdated="28 de junio de 2026"
    >
      <p className="text-muted-foreground">
        Estos Términos y Condiciones (los &quot;Términos&quot;) regulan el acceso y uso de la plataforma
        FinScope, disponible en app.finscope.mx. Al acceder o utilizar FinScope aceptas estos Términos.
      </p>

      <LegalSection number={1} title="Aceptación de los términos">
        <p>
          Al iniciar sesión, acceder o utilizar FinScope, confirmas que has leído, entendido y aceptado estos
          Términos y la Política de Privacidad. Si utilizas la plataforma en nombre de una organización,
          declaras contar con autoridad para aceptar estos Términos en su representación. Si no estás de
          acuerdo, no debes utilizar la plataforma.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Descripción del servicio">
        <p>
          FinScope es una plataforma de software como servicio (SaaS) de gestión y análisis financiero
          empresarial que incluye, entre otras funcionalidades: dashboard ejecutivo, movimientos, categorías,
          clientes, flujo de efectivo (cash flow), estado de resultados (P&amp;L), facturación, pagos,
          prioridades y rentabilidad, así como integraciones opcionales con servicios externos. FinScope no es
          un banco, institución financiera, asesor financiero, contador ni auditor, y su contenido no
          constituye asesoría financiera, contable, fiscal ni legal.
        </p>
      </LegalSection>

      <LegalSection number={3} title="Registro y cuentas">
        <p>
          Las cuentas de FinScope son aprovisionadas por el equipo de FinScope o por el administrador de tu
          organización. Cada cuenta es personal e intransferible. Debes proporcionar información veraz y
          mantenerla actualizada. FinScope asigna roles y permisos por organización; el alcance de lo que cada
          usuario puede ver y hacer depende del rol asignado.
        </p>
      </LegalSection>

      <LegalSection number={4} title="Responsabilidades del usuario">
        <ul className="list-disc pl-6 space-y-2">
          <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
          <li>Utilizar la plataforma únicamente para fines lícitos y conforme a estos Términos.</li>
          <li>Garantizar que cuenta con los derechos necesarios sobre la información que carga o conecta.</li>
          <li>Notificar de inmediato cualquier uso no autorizado de su cuenta.</li>
        </ul>
      </LegalSection>

      <LegalSection number={5} title="Uso permitido">
        <p>Queda prohibido, de manera enunciativa mas no limitativa:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Intentar acceder a información de otras organizaciones o cuentas.</li>
          <li>Vulnerar, sondear o eludir los mecanismos de seguridad de la plataforma.</li>
          <li>Realizar ingeniería inversa, copiar o revender la plataforma sin autorización.</li>
          <li>Utilizar la plataforma para actividades ilícitas o para cargar contenido malicioso.</li>
          <li>Sobrecargar deliberadamente la infraestructura del servicio.</li>
        </ul>
      </LegalSection>

      <LegalSection number={6} title="Información y datos proporcionados por el usuario">
        <p>
          Tú y tu organización conservan la titularidad de la información que cargan o conectan a FinScope. Al
          usar la plataforma, otorgas a FinScope una licencia limitada para procesar dicha información con el
          único fin de prestar el servicio. Eres responsable de la exactitud, calidad y licitud de la
          información proporcionada; FinScope procesa y presenta la información tal como fue suministrada.
        </p>
      </LegalSection>

      <LegalSection number={7} title="Integraciones con terceros">
        <p>
          FinScope puede ofrecer integraciones opcionales con servicios de terceros. La activación de una
          integración es decisión del usuario autorizado de cada organización. El uso de cada servicio externo
          se rige, además, por los términos y políticas del tercero correspondiente. FinScope no es responsable
          de la disponibilidad, exactitud o funcionamiento de los servicios de terceros.
        </p>
      </LegalSection>

      <LegalSection number={8} title="Google y servicios externos">
        <p>
          Si decides conectar una cuenta de Google, la autorización se realiza mediante Google OAuth 2.0
          directamente con Google. FinScope solicita únicamente permisos de solo lectura sobre Google Drive y
          Google Sheets para las funcionalidades que actives, almacena las credenciales otorgadas de forma
          cifrada y no solicita ni almacena tu contraseña de Google. Puedes revocar el acceso en cualquier
          momento desde la plataforma o desde la configuración de tu cuenta de Google. El tratamiento de la
          información recibida de las APIs de Google se describe en nuestra Política de Privacidad.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Disponibilidad del servicio">
        <p>
          FinScope procura mantener la plataforma disponible de forma continua; sin embargo, no garantiza una
          disponibilidad ininterrumpida. El servicio puede suspenderse temporalmente por mantenimiento,
          actualizaciones, causas de fuerza mayor o fallas de proveedores externos. Procuraremos minimizar el
          impacto de dichas interrupciones.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Propiedad intelectual">
        <p>
          La plataforma FinScope, incluyendo su software, diseño, marca, logotipos e interfaces, es propiedad
          de sus titulares y está protegida por las leyes de propiedad intelectual aplicables. Estos Términos
          no te otorgan ningún derecho de propiedad sobre la plataforma, únicamente un derecho de uso limitado,
          revocable y no exclusivo mientras mantengas una cuenta activa.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Contenido y datos del usuario">
        <p>
          La información financiera y de negocio que tu organización gestione en FinScope pertenece a tu
          organización. FinScope no utilizará dicha información para fines distintos a la prestación del
          servicio, conforme a la Política de Privacidad. Al terminar la relación, puedes solicitar la
          eliminación de la información según el procedimiento descrito en la Política de Privacidad.
        </p>
      </LegalSection>

      <LegalSection number={12} title="Seguridad de las credenciales">
        <p>
          Eres responsable de resguardar tus credenciales de acceso y de las acciones realizadas desde tu
          cuenta. FinScope nunca te pedirá tu contraseña por correo u otros medios. Si sospechas un acceso no
          autorizado, deberás notificarlo de inmediato al administrador de tu organización o al equipo de
          FinScope para tomar las medidas correspondientes.
        </p>
      </LegalSection>

      <LegalSection number={13} title="Limitaciones y responsabilidades">
        <p>
          FinScope se proporciona &quot;tal cual&quot; y &quot;según disponibilidad&quot;. En la máxima medida
          permitida por la ley aplicable, FinScope no será responsable por daños indirectos, incidentales o
          consecuenciales, ni por decisiones de negocio, financieras, fiscales o legales tomadas con base en la
          información presentada por la plataforma, la cual depende de los datos proporcionados por el propio
          usuario. Nada en estos Términos limita responsabilidades que no puedan limitarse conforme a la ley.
        </p>
      </LegalSection>

      <LegalSection number={14} title="Suspensión o terminación de cuentas">
        <p>
          FinScope podrá suspender o cancelar cuentas que incumplan estos Términos, comprometan la seguridad de
          la plataforma o de otros usuarios, o hagan un uso indebido del servicio. Cuando sea razonable,
          notificaremos previamente y daremos oportunidad de corregir el incumplimiento. Tu organización puede
          solicitar la baja del servicio en cualquier momento.
        </p>
      </LegalSection>

      <LegalSection number={15} title="Cambios al servicio">
        <p>
          FinScope evoluciona constantemente. Podemos agregar, modificar o retirar funcionalidades para mejorar
          la plataforma. Si un cambio afecta de manera sustancial funcionalidades esenciales contratadas, lo
          comunicaremos por medios razonables.
        </p>
      </LegalSection>

      <LegalSection number={16} title="Cambios a los términos">
        <p>
          Podemos actualizar estos Términos ocasionalmente. La versión vigente estará siempre publicada en esta
          página con su fecha de última actualización. El uso continuado de la plataforma después de la
          publicación de cambios constituye la aceptación de los mismos.
        </p>
      </LegalSection>

      <LegalSection number={17} title="Contacto">
        <p>
          Para dudas sobre estos Términos, contáctanos a través de los canales de atención disponibles dentro
          de la plataforma o mediante el administrador de la cuenta de tu organización, quien canalizará tu
          solicitud con el equipo de FinScope.
        </p>
      </LegalSection>

      <LegalSection number={18} title="Ley aplicable y jurisdicción">
        <p>
          Estos Términos se interpretan conforme a la legislación aplicable en México, país desde el cual se
          ofrece la plataforma, sin perjuicio de las normas imperativas que resulten aplicables en el lugar de
          residencia del usuario. Cualquier controversia se procurará resolver primero de manera amistosa entre
          las partes.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
